import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const VerifyMagicLink: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double verification (React StrictMode or re-renders)
    if (verificationAttempted.current) {
      return;
    }

    const token = searchParams.get('token');

    if (!token) {
      setError('Invalid magic link - no token provided');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    verificationAttempted.current = true;

    const verify = async () => {
      try {
        await verifyMagicLink(token);
        // verifyMagicLink already navigates to /dashboard in AuthContext
        // No need to navigate again here
      } catch (err: any) {
        console.error('Magic link verification failed:', err);
        setError(err.response?.data?.error || 'Invalid or expired magic link');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    verify();
  }, [searchParams, navigate, verifyMagicLink]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          mx: 2,
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Redirecting to login...
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Verifying Magic Link
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we sign you in...
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyMagicLink;
